import { useEffect, useSyncExternalStore } from "react";
import { GET } from "../utils/apirequest";
import { API } from "./API";
import { CountryDto, setCountries } from "./countries";
import { ZoneDto, setZones } from "./zones";
import { RegionDto, setRegions } from "./regions";

// Loads countries, zones and regions from E-Paper Settings into the live
// lists in config/countries.ts, config/zones.ts and config/regions.ts, and
// re-renders subscribers.

const MAX_AGE_MS = 60 * 1000;

let version = 0;
let loadedAt = 0;
let inflight: Promise<void> | null = null;
const listeners = new Set<() => void>();

const notify = () => {
  version += 1;
  listeners.forEach((l) => l());
};

export const loadEpaperConfig = (force = false): Promise<void> => {
  if (inflight) return inflight;
  if (!force && loadedAt && Date.now() - loadedAt < MAX_AGE_MS) {
    return Promise.resolve();
  }
  inflight = (async () => {
    try {
      const res: any = await GET(API.EPAPER_SETTINGS_CONFIG, null, null);
      const data = res?.data || {};
      setCountries((data.countries || []) as CountryDto[]);
      if (Array.isArray(data.countries) && data.countries.length) {
        setZones((data.zones || []) as ZoneDto[]);
      }
      // Older backends send no regions; keep the current list then.
      if (Array.isArray(data.regions)) setRegions(data.regions as RegionDto[]);
      loadedAt = Date.now();
      notify();
    } catch (err) {
      // Keep the current (or fallback) lists; forms stay usable.
      console.error("Failed to load e-paper settings:", err);
    } finally {
      inflight = null;
    }
  })();
  return inflight;
};

const subscribe = (l: () => void) => {
  listeners.add(l);
  return () => {
    listeners.delete(l);
  };
};

// Re-renders the caller whenever countries/zones reload. Pass `refreshKey`
// (e.g. a modal's `open` flag) to force a fresh load when it turns truthy.
export const useEpaperConfig = (refreshKey?: unknown): number => {
  const v = useSyncExternalStore(subscribe, () => version);
  useEffect(() => {
    if (refreshKey === undefined) loadEpaperConfig();
    else if (refreshKey) loadEpaperConfig(true);
  }, [refreshKey]);
  return v;
};
